import React, { Fragment } from 'react';
import {
  arrayOf,
  shape,
  string,
  oneOf,
  number,
} from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import {
  Heading,
  Icon,
  SegmentedControl,
  Small,
  Collapsible,
  Button,
} from 'components/common';
import withApplicationConfig from 'containers/withApplicationConfig';
import Tooltip from 'components/common/Tooltip';

import './YrrColumn.scss';

// TODO(victor): It may not be good to hardcode "Cash Rebate" & "Airmiles" all
// over the place. Hopefully can wrap them into some arrays

const YRR_COLUMN = {
  CASH_REBATE_REWARD: 'CASH_REBATE_REWARD',
  AIR_MILES_REWARD: 'AIR_MILES_REWARD',
};

const YrrListItem = ({
  value,
  notApplicableLabel,
  tooltip,
  formatting: {
    prefix = '',
    suffix = '',
  } = {},
  rewardValue,
}) => {
  const compiledRewardValue = _.template(rewardValue);
  const prefixSuffixVal = compiledRewardValue({
    prefix,
    value,
    suffix,
  });

  const formattedValue = value ? prefixSuffixVal : notApplicableLabel;

  return (
    <Fragment>
      <div>{formattedValue}</div>
      {tooltip && (
        <div className="yrr-list-item-tooltip">
          <Tooltip
            text={tooltip}
          >
            <Icon secondary name="info" />
          </Tooltip>
        </div>
      )}
    </Fragment>
  );
};

YrrListItem.propTypes = {
  value: string,
  notApplicableLabel: string,
  tooltip: string,
  formatting: shape({
    prefix: string,
    suffix: string,
  }),
  rewardValue: string.isRequired,
};

YrrListItem.defaultProps = {
  value: undefined,
  notApplicableLabel: undefined,
  tooltip: undefined,
  formatting: {},
};

const YrrListItems = ({
  type,
  columns = [],
  selectedColumnOnMobile,
  displayColVals,
  labels,
  rewardValue,
}) => {
  const cashRebateClasses = {
    'yrr-list-item-cash-rebate': true,
    'show-on-mobile': selectedColumnOnMobile === YRR_COLUMN.CASH_REBATE_REWARD,
  };

  const airmilesClasses = {
    'yrr-list-item-airmiles': true,
    'show-on-mobile': selectedColumnOnMobile === YRR_COLUMN.AIR_MILES_REWARD,
  };

  const columnHeaderClasses = {
    'yrr-list-item-name': true,
    'single-column': columns.length === 1,
  };

  const showCol = displayColVals.cashRewardBaseValue || displayColVals.airMilesRewardBaseValue;

  if (!showCol) {
    return null;
  }

  return (
    <div className="yrr-list-item">
      <div className={classNames(columnHeaderClasses)}>
        <strong>{displayColVals.displayVal}</strong>
      </div>
      {columns.includes(YRR_COLUMN.CASH_REBATE_REWARD) && (
      <div className={classNames(cashRebateClasses)}>
        <YrrListItem
          type={_.find(type, { key: YRR_COLUMN.CASH_REBATE_REWARD })}
          tooltip={displayColVals.cashToolTip}
          value={displayColVals.cashRewardBaseValue}
          notApplicableLabel={labels.notApplicableValue}
          formatting={labels.cashRebateReward}
          rewardValue={rewardValue}
        />
      </div>
      )}
      {columns.includes(YRR_COLUMN.AIR_MILES_REWARD) && (
      <div className={classNames(airmilesClasses)}>
        <YrrListItem
          type={_.find(type, { key: YRR_COLUMN.AIR_MILES_REWARD })}
          tooltip={displayColVals.airMilesToolTip}
          value={displayColVals.airMilesRewardBaseValue}
          notApplicableLabel={labels.notApplicableValue}
          formatting={labels.airMilesReward}
          rewardValue={rewardValue}
        />
      </div>
      )}
    </div>
  );
};

YrrListItems.defaultProps = {
  selectedColumnOnMobile: YRR_COLUMN.CASH_REBATE_REWARD,
  labels: {},
  type: [],
  columns: [],
  displayColVals: {},
};

YrrListItems.propTypes = {
  labels: shape({
    notApplicableValue: string,
  }),
  selectedColumnOnMobile: oneOf([
    YRR_COLUMN.CASH_REBATE_REWARD,
    YRR_COLUMN.AIR_MILES_REWARD,
  ]),
  type: arrayOf(shape({})),
  columns: arrayOf(string),
  displayColVals: shape({}),
  rewardValue: string.isRequired,
};

class YrrColumn extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedSegment: null,
      isShowMore: false,
    };

    this.onSegmentedControlChange = this.onSegmentedControlChange.bind(this);
    this.toggleIsShowMore = this.toggleIsShowMore.bind(this);
    this.setSelectedSegment = this.setSelectedSegment.bind(this);
    this.reviseYrrs = this.reviseYrrs.bind(this);
    this.modifyToolTipBaseVal = this.modifyToolTipBaseVal.bind(this);
  }

  componentDidMount() {
    const { yrrs } = this.props;
    const segments = this.getDisplayColumns(yrrs);
    this.setSelectedSegment(segments);
  }

  onSegmentedControlChange({ value }) {
    this.setState({
      selectedSegment: value,
    });
  }

  getDisplayColumns(yrrs) {
    const newarr = yrrs.map(({ type }) => type.map(({ key }) => key));
    // eslint-disable-next-line
    const merged = [].concat.apply([], newarr);

    return _.uniq(merged);
  }

  getSegmentOptions(displayColumns, labels) {
    return Object.values(YRR_COLUMN)
      .map(value => ({
        label: labels[value],
        value,
        disabled: !displayColumns.includes(value),
      }));
  }

  setSelectedSegment(segements) {
    if (segements.includes(YRR_COLUMN.CASH_REBATE_REWARD)) {
      this.setState({
        selectedSegment: YRR_COLUMN.CASH_REBATE_REWARD,
      });
    } else if (segements.includes(YRR_COLUMN.AIR_MILES_REWARD)) {
      this.setState({
        selectedSegment: YRR_COLUMN.AIR_MILES_REWARD,
      });
    }
  }

  toggleIsShowMore() {
    this.setState(prevState => ({
      isShowMore: !prevState.isShowMore,
    }));
  }

  modifyToolTipBaseVal(yrrObj, val) {
    const obj = yrrObj;
    if (val.type[0].key === 'CASH_REBATE_REWARD') {
      obj.displayColVals.cashRewardBaseValue = val.rewardValue.rewardBaseValue;
      obj.displayColVals.cashToolTip = val.description.tooltip;
    } else if (val.type[0].key === 'AIR_MILES_REWARD') {
      obj.displayColVals.airMilesRewardBaseValue = val.rewardValue.rewardBaseValue;
      obj.displayColVals.airMilesToolTip = val.description.tooltip;
    }
  }

  reviseYrrs(yrrs) {
    const spendingTypeObj = {};
    const revisedYrrs = [];
    yrrs.forEach((val) => {
      val.spendingType.forEach((type) => {
        let newObj;
        if (spendingTypeObj[type.key]) {
          const existingObj = revisedYrrs.find(obj => obj.displayColVals.key === type.key);
          this.modifyToolTipBaseVal(existingObj, val);
        } else {
          spendingTypeObj[type.key] = true;
          newObj = Object.assign({}, val);
          newObj.displayColVals = {};
          newObj.displayColVals.key = type.key;
          newObj.displayColVals.displayVal = type.display;
          newObj.displayColVals.cashRewardBaseValue = '';
          newObj.displayColVals.airMilesRewardBaseValue = '';
          newObj.displayColVals.cashToolTip = '';
          newObj.displayColVals.airMilesToolTip = '';

          this.modifyToolTipBaseVal(newObj, val);
          revisedYrrs.push(newObj);
        }
      });
    });

    return revisedYrrs;
  }

  render() {
    const {
      selectedSegment,
      isShowMore,
    } = this.state;
    const {
      yrrs: yrrsRaw = [],
      labels = {},
      rewardValue,
    } = this.props;

    const yrrs = yrrsRaw.filter(({ type }) => _.find(type, { key: YRR_COLUMN[_.get(type, '[0].key', '')] }));
    const revisedYrrs = this.reviseYrrs(yrrs);
    const { toggleLabels, showMoreThreshold } = labels;
    const initialYrrs = revisedYrrs.slice(0, showMoreThreshold);
    const collapsedYrrs = revisedYrrs.slice(showMoreThreshold);
    const accordionLabel = isShowMore ? toggleLabels.showLess : toggleLabels.showMore;
    const displayColumns = this.getDisplayColumns(yrrs);
    const segmentOptions = this.getSegmentOptions(displayColumns, labels);

    const placeholderClassNames = classNames('placeholder', {
      'single-column': displayColumns.length === 1,
    });
    const listItemsClassNames = classNames({
      'alpha-layer': !isShowMore && collapsedYrrs > 0,
    });
    const disabledOptionValues = _.filter(segmentOptions, { disabled: true }).map(x => x.value);

    return (
      <div className="yrr-column">
        <div className="yrr-column-title">
          <Heading as="h3">
            {labels.yearRoundRewards}
          </Heading>
        </div>
        <div className="yrr-segmented-control">
          <SegmentedControl
            fluid
            small
            value={selectedSegment}
            disabledOptionValues={disabledOptionValues}
            options={segmentOptions}
            onChange={this.onSegmentedControlChange}
            dataSelectorId="yrr-segmented"
          />
        </div>
        <div className="yrr-column-header">
          <div className={placeholderClassNames} />
          {displayColumns.includes(YRR_COLUMN.CASH_REBATE_REWARD) && (
            <div className="cash-rebate-header">
              <Small secondary>
                <strong>{labels.CASH_REBATE_REWARD}</strong>
              </Small>
            </div>
          )}
          {displayColumns.includes(YRR_COLUMN.AIR_MILES_REWARD) && (
            <div className="airmiles-header">
              <Small secondary>
                <strong>{labels.AIR_MILES_REWARD}</strong>
              </Small>
            </div>
          )}
        </div>
        <div className={listItemsClassNames}>
          {initialYrrs.map(yrr => (
            <YrrListItems
              key={yrr.displayColVals.key}
              columns={displayColumns}
              {...yrr}
              labels={labels}
              selectedColumnOnMobile={selectedSegment}
              rewardValue={rewardValue}
            />
          ))}
          <Collapsible isCollapsed={!isShowMore}>
            {collapsedYrrs.map(yrr => (
              <YrrListItems
                key={yrr.displayColVals.key}
                columns={displayColumns}
                {...yrr}
                labels={labels}
                selectedColumnOnMobile={selectedSegment}
                rewardValue={rewardValue}
              />
            ))}
          </Collapsible>
        </div>
        {revisedYrrs.length > showMoreThreshold && (
          <div className="show-more-button-wrapper">
            <Button
              text
              rightIconName={isShowMore ? 'chevron_up' : 'chevron_down'}
              onClick={this.toggleIsShowMore}
            >
              {accordionLabel}
            </Button>
          </div>
        )}
      </div>
    );
  }
}

YrrColumn.defaultProps = {
  yrrs: [],
  labels: {
    toggleLabels: {
      showMore: 'Show more',
      showLess: 'Show less',
    },
    showMoreThreshold: 1,
  },
};

YrrColumn.propTypes = {
  yrrs: arrayOf(
    shape({
      title: string,
      cashRebate: shape({
        value: string,
        tooltip: string,
      }),
      airmiles: shape({
        value: string,
        tooltip: string,
      }),
    })
  ),
  labels: shape({
    showMore: string,
    showLess: string,
    notApplicableLabel: string,
    airMilesRewards: string,
    cashRebate: string,
    yearRoundRewards: string,
    showMoreThreshold: number,
    toggleLabels: shape({
      showMore: string,
      showLess: string,
    }),
  }),
  rewardValue: string.isRequired,
};

export default withApplicationConfig(YrrColumn, 'productPage');
